import React, { useMemo, useContext, useRef, useState } from "react";
import { Form, FormDate, FormRow, FormLabel, FormText, FormRadio, RawFormRadio } from "../components/FormElements";
import Title from "../components/Title";
import CenterContainer from "../components/CenterContainer";
import nl from "date-fns/locale/nl";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faInfoCircle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { Link, useHistory } from "react-router-dom";
import {
    UserRegisterRequest,
    calculateAge,
    getLanguageForHostname,
    UserLoginRequest,
    UserRegisterRequestSchema,
    UserLoginRequestSchema,
    getSpeakLanguageForHostname,
} from "consumerhouse-shared";
import AuthContext from "../AuthContext";
import styled from "styled-components";
import { Translation, useLanguage } from "../useLanguage";
import { RegisterFriendContext } from "../RegisterFriendContext";
import { AnyListener, FormError, Listener, useForm } from "typed-react-form";
import { CollapseContainer } from "../components/CollapseContainer";
import { ErrorBalloon } from "../components/Balloon";
import Alert from "../components/Alert";
import { LinkButton } from "../components/LinkButton";
import { ReactComponent as UndrawEnter } from "../data/undraw_enter.svg";
import { UndrawContainer } from "../components/UndrawContainer";
import { ButtonContainer } from "../components/ButtonContainer";
import { RateLimitError, ValidationError } from "../api";
import { LanguageContext } from "../LanguageContext";
import { TermsLink } from "../components/TermsLink";

const FoundContainer = styled.div`
    display: flex;
    flex-direction: column;

    &.error {
        border: 2px solid red;
        border-radius: 1em;
    }
`;

const DontForget = styled.p`
    font-weight: bold;
    font-size: 0.9em;
    opacity: 0.7;
    text-align: right;
`;

const Card = styled.div`
    background: #0001;
    border-radius: 1em;
    padding: 1em;
`;

type LoginOrRegisterProps = {
    message?: string;
    defaultMode?: "login" | "register";
};

const HOWFOUND: Translation[] = [
    "register-howfound-email",
    "register-howfound-friends",
    "register-howfound-outside-ads",
    "register-howfound-vehicle-ads",
    "register-howfound-facebook",
    "register-howfound-instagram",
    "register-howfound-social-media",
    "register-howfound-ct",
    "register-howfound-other",
    "register-howfound-student",
    "register-howfound-student2",
    "register-howfound-student3",
    "register-howfound-student4",
    "register-howfound-student5",
    "register-howfound-student6",
    "register-howfound-student7",
    "register-howfound-student8",
    "register-howfound-student9",
    "register-howfound-student10",
    "register-howfound-student11",
    "register-howfound-student12",
    "register-howfound-student13",
    "register-howfound-student14",
    "register-howfound-student15",
    "register-howfound-student16",
    "register-howfound-student17",
    "register-howfound-student18",
    "register-howfound-student19",
    "register-howfound-student20",
    "register-howfound-student21",
    "register-howfound-student22",
    "register-howfound-student23",
    "register-howfound-student24",
    "register-howfound-student25",
    "register-howfound-student26",
    "register-howfound-student27",
    "register-howfound-student28",
    "register-howfound-student29",
    "register-howfound-student30",
    "register-howfound-student31",
    "register-howfound-student32",
];

export function LoginOrRegister(props: LoginOrRegisterProps) {
    const [mode, setMode] = useState<"login" | "register">(props.defaultMode ?? "login"); //props.location.hash === "#register" ? "register" : "login"
    const language = useLanguage();

    return (
        <CenterContainer width="500px" style={{ margin: "2em 0" }}>
            {window.innerWidth > 600 && (
                <UndrawContainer>
                    <UndrawEnter />
                </UndrawContainer>
            )}
            {props.message && (
                <Alert style={{ flexWrap: "nowrap" }} color="#e38b29" textColor="white" icon={faExclamationCircle}>
                    {props.message}
                </Alert>
            )}
            {mode === "register" ? (
                <>
                    <Title>{language("register")}</Title>
                    <Register />
                    <Button type="button" color={"#666"} icon={faArrowRight} onClick={() => setMode("login")}>
                        {language("register-has-account")}
                    </Button>
                </>
            ) : (
                <>
                    <Title>{language("login")}</Title>
                    <Login />
                    <Button type="button" style={{ margin: "1em 0" }} color={"#666"} icon={faArrowRight} onClick={() => setMode("register")}>
                        {language("login-no-account")}
                    </Button>
                </>
            )}
        </CenterContainer>
    );
}

function Register() {
    const history = useHistory();
    const { client, auth, setAuth } = useContext(AuthContext);
    const { language: currentLanguage } = useContext(LanguageContext);
    const language = useLanguage();
    const registerFriendId = useContext(RegisterFriendContext);
    const [defaultHowFound, hideHowFound] = useMemo(() => {
        let params = new URLSearchParams(window.location.search);
        return [params.get("howFound") || undefined, params.get("hideHowFound") === "1"];
    }, [window.location.search]);
    const form = useForm<UserRegisterRequest>(
        {
            registerFriendId: registerFriendId,
            language: getSpeakLanguageForHostname(window.location.hostname),
            howFound: defaultHowFound ? language(defaultHowFound as Translation) : undefined,
        } as any,
        (data) => UserRegisterRequestSchema.validate(data)
    );
    const foundContainerRef = useRef<HTMLDivElement>(null);

    async function submit() {
        await form.validate();
        if (form.error) {
            window.scrollTo(0, 0);
            return;
        }

        form.setState({ isSubmitting: true });
        try {
            let res = await client.fetch("/user/register", form.values);
            if (res.status === "error") {
                form.setErrors(res);
            } else if (res.status === "unset") {
                history.push("/enable-account/" + encodeURIComponent(form.values.email));
            } else {
                setAuth(res);
            }
        } catch (ex) {
            if (ex instanceof RateLimitError) {
                form.setError("firstName", language("register-limit"));
            } else {
                console.error("Could not register", ex);
            }
        }
        form.setState({ isSubmitting: false });
    }

    return (
        <Form
            onSubmit={(ev) => {
                ev.preventDefault();
                submit();
            }}>
            <FormRow>
                <FormLabel htmlFor="firstName">{language("register-firstname")}</FormLabel>
                <FormText form={form} name="firstName" autoFocus placeholder="" style={{ flexGrow: 1 }} />
            </FormRow>
            <FormRow>
                <FormLabel htmlFor="lastName">{language("register-lastname")}</FormLabel>
                <FormText form={form} name="lastName" placeholder="" style={{ flexGrow: 1 }} />
            </FormRow>
            <FormRow>
                <FormLabel htmlFor="email">{language("register-email")}</FormLabel>
                <FormText form={form} name="email" placeholder="someone@example.com" style={{ flexGrow: 1 }} />
            </FormRow>
            <FormRow>
                <FormLabel htmlFor="email">{language("register-telephone")}</FormLabel>
                <FormText form={form} name="telephoneNumber" placeholder="0032xxxxxxxxx" style={{ flexGrow: 1 }} />
            </FormRow>
            <FormRow>
                <FormLabel>{language("register-birthdate")}</FormLabel>
                <FormDate min="1900-01-01" max={new Date().toISOString().split("T")[0]} form={form} name="birthDate" />
            </FormRow>
            <FormRow>
                <FormLabel>{language("register-gender")}</FormLabel>
                <FormRow>
                    <FormRadio form={form} name="gender" value="male" style={{ flexGrow: 1 }}>
                        {language("register-gender-male")}
                    </FormRadio>
                    <FormRadio form={form} name="gender" value="female" style={{ flexGrow: 1 }}>
                        {language("register-gender-female")}
                    </FormRadio>
                </FormRow>
            </FormRow>
            <FormRow>
                <FormLabel htmlFor="iban">{language("register-iban")}</FormLabel>
                <FormText id="iban" form={form} name="iban" placeholder="BExx xxxx xxxx xxxx" style={{ flexGrow: 1 }} />
            </FormRow>
            <FormRow>
                <FormLabel htmlFor="password">{language("register-password")}</FormLabel>
                <FormText form={form} id="password" name="password" type="password" placeholder={language("login-secret")} style={{ flexGrow: 1 }} />
            </FormRow>
            <DontForget>{language("register-password-noforget")}</DontForget>
            <FormRow>
                <Listener
                    form={form}
                    name="howFound"
                    render={({ value, error }) =>
                        (!hideHowFound || !value) && (
                            <>
                                <FormLabel style={{ maxWidth: "unset" }}>{language("register-howfound")}</FormLabel>
                                <FoundContainer ref={foundContainerRef}>
                                    {HOWFOUND.map((e, i) => (
                                        <FormRadio key={i} form={form} value={language(e)} name="howFound" type="radio">
                                            {language(e)}
                                        </FormRadio>
                                    ))}
                                </FoundContainer>
                                <ErrorBalloon
                                    targetRef={foundContainerRef}
                                    visible={!!error}
                                    onChange={(v, ref) => (v ? ref.classList.add("error") : ref.classList.remove("error"))}>
                                    {language(error as Translation)}
                                </ErrorBalloon>
                            </>
                        )
                    }
                />
            </FormRow>
            <CollapseContainer style={{ margin: "1em 0" }} title={language("register-faq-iban-title")}>
                <p>{language("register-faq-iban")}</p>
            </CollapseContainer>
            <TermsLink />
            <AnyListener
                form={form}
                render={() => (
                    <Button style={{ margin: "1em 0" }} type="submit" loading={form.state.isSubmitting} disabled={form.state.isSubmitting}>
                        {language("register")}
                    </Button>
                )}
            />
        </Form>
    );
}

function Login() {
    const { client, setAuth, auth } = useContext(AuthContext);
    const language = useLanguage();
    const form = useForm({ email: "", password: "" } as UserLoginRequest, (data) => UserLoginRequestSchema.validate(data));
    const history = useHistory();

    async function submit() {
        await form.validate();
        if (form.error) return;
        form.setState({ isSubmitting: true });
        try {
            let res = await client.fetch("/user/login", form.values);
            if (res.status === "error") {
                form.setErrors({
                    email: res.email ? language(res.email as Translation) : undefined,
                    password: res.password ? language(res.password as Translation) : undefined,
                });
            } else if (res.status === "unset") {
                history.push("/enable-account/" + encodeURIComponent(form.values.email));
            } else {
                setAuth(res);
            }
        } catch (ex) {
            if (ex instanceof RateLimitError) {
                form.setError("password", language("login-limit"));
            } else {
                console.error("Could not login", ex);
            }
        }
        form.setState({ isSubmitting: false });
    }

    return (
        <Card>
            <Form
                onSubmit={(ev) => {
                    ev.preventDefault();
                    submit();
                }}>
                <FormRow>
                    <FormLabel>{language("login-email")}</FormLabel>
                    <FormText
                        autoFocus
                        name="email"
                        form={form}
                        style={{
                            flexGrow: 1,
                        }}
                        type="email"
                        placeholder="someone@example.com"
                    />
                </FormRow>
                <FormRow>
                    <FormLabel>{language("login-password")}</FormLabel>
                    <FormText
                        name="password"
                        form={form}
                        style={{
                            flexGrow: 1,
                        }}
                        type="password"
                        placeholder={language("login-secret")}
                    />
                </FormRow>
                <Link to="/reset-password" style={{ textAlign: "right" }}>
                    <LinkButton>{language("login-reset-password")}</LinkButton>
                </Link>

                <AnyListener
                    form={form}
                    render={({ state }) => (
                        <Button disabled={state.isSubmitting} type="submit" style={{ margin: "0.5em 0" }}>
                            {state.isSubmitting && <Spinner style={{ margin: "0 0.5em" }} />}
                            {language("login")}
                        </Button>
                    )}
                />
            </Form>
        </Card>
    );
}
